import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Tooltip,
  Grid2,
  Box,
  Typography,
} from '@mui/material';
import StatusChip from '../../customTableComponent/statusChip';
import { getLaparoscopyList, ProcedureSummary } from './laparoscopy.helper';

import EditButton from '../../../assets/SVG/editPencil.svg';
import viewIcon from '../../../assets/SVG/eye_view.svg';
import PrescribedUnavailable from '../prescribedUnavailable';
import { usePatientContext } from '../../SubPatinetProvider';

const LaparoscopyList = ({
  onEdit,
  onView,
}: {
  onEdit: (id: number) => void;
  onView: (id: number) => void;
}) => {
  const { patientId } = usePatientContext();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [laparoscopyDetails, setLaparoscopyDetails] = useState<any[]>([]);
  const [procedureSummary, setProcedureSummary] = useState<ProcedureSummary | null>(null);


  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch laparoscopy details
    getLaparoscopyList(patientId)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((response: any) => {
        setLaparoscopyDetails(response.objects);
        setProcedureSummary(response.procedure_summary);

      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error fetching laparoscopy details:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [patientId]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <CircularProgress />
      </div>
    );
  }


  if (!laparoscopyDetails.length) {
    return <PrescribedUnavailable message="No laparoscopy has been prescribed yet" />; // Show PrescribedUnavailable when no data is available
  }
  
  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                height: '2rem',
                background: 'rgba(250, 250, 250, 1)',
                '& .MuiTableCell-root': {
                  padding: '0.6rem 0.6rem',
                },
              }}
            >
              {['Date of Procedure', 'Procedure ID', 'Surgeon Name', 'Status', ''].map(
                (title, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      fontFamily: 'Nunito, sans-serif',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      lineHeight: '1.45',
                      textAlign: 'left',
                      color: 'rgba(35, 35, 35, 1)',
                    }}
                  >
                    {title}
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {laparoscopyDetails.map((laparoscopy) => (
              <TableRow key={laparoscopy.id}>
                <TableCell>
                  {new Date(laparoscopy.created_on).toLocaleDateString('en-US')}
                </TableCell>
                <TableCell>{laparoscopy?.services[0]?.code}</TableCell>
                <TableCell>{laparoscopy.doctor_name}</TableCell>
                <TableCell>
                  <StatusChip
                    value={laparoscopy.laparoscopy_status ? 'Completed' : 'Pending'}
                  />
                </TableCell>

                <TableCell>
                  {laparoscopy.laparoscopy_status ? (
                    <Tooltip title="View Details">
                      <img 
                        src={viewIcon} 
                        style={{ width: '20px', height: '20px', cursor: 'pointer' }} 
                        alt="View Icon" 
                        onClick={() => onView(laparoscopy.id)} 
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Edit Details">
                      <img 
                        src={EditButton} 
                        style={{ width: '20px', height: '20px', cursor: 'pointer' }} 
                        alt="Edit Icon" 
                        onClick={() => onEdit(laparoscopy.id)} 
                      />
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>


      {procedureSummary?.laparoscopy && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 300,
            right: 0,
            px: 4,
            py: 2,
            pb: 4,
            bgcolor: '#fff',
          }}
        >
          <Grid2 container alignItems="center" justifyContent="space-between">
            <Grid2>
              <Typography fontWeight={600} fontSize={16}>
          Total Count :{' '}
                <Typography component="span" fontWeight={500}>
                  {(procedureSummary.laparoscopy.completed ?? 0) +
              (procedureSummary.laparoscopy.pending ?? 0)}
                </Typography>
              </Typography>
            </Grid2>

            <Grid2 container justifyContent="flex-end" spacing={3}>
              {[
                {
                  label: 'Complete',
                  count: procedureSummary.laparoscopy.completed ?? 0,
                  color: '#47B35F',
                },
                {
                  label: 'Pending',
                  count: procedureSummary.laparoscopy.pending ?? 0,
                  color: '#EFAE78',
                },
              ].map(({ label, count, color }) => (
                <Grid2 key={label}>
                  <Box display="flex" alignItems="center" minWidth={100}>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        bgcolor: color,
                        mr: 1,
                      }}  
                    />
                    <Typography fontWeight={600} fontSize={14}>
                      {label}{' '}
                      <Typography component="span" fontWeight={600}  fontSize={13}>
                        {count.toString().padStart(2, '0')}
                      </Typography>
                    </Typography>
                  </Box>
                </Grid2>
              ))}
            </Grid2>
          </Grid2>
        </Box>
      )}  
    </>
  );
};

export default LaparoscopyList;
